import { NavLink, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { GameRoomStatus, isGameRoomSessionActive } from '@/redux/types/room'
import { useAppSelector } from '@/redux/hooks'
import RoomNotFound from '../PageNotFound/RoomNotFound'
import { TimerControls } from '../settings/timer-configuration/TimerControls'
import { useGamePhase } from '@/hooks/useGamePhase'
import { GameplayLength } from '../settings/timer-configuration/GameplayLength'
import QuickAddEvent from '../scenario/QuickAddEvent'
import { EndGameModal } from './EndGameModal'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { EndGameButton } from './EndGameButton'
import { ArrowPathIcon, BellIcon } from '@heroicons/react/24/outline'
import StartGameButton from './StartGameButton'
import { Button } from '../ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import { useRoomControls } from '@/hooks/useRoomControls'

import { useCallback, useEffect, useState } from 'react'
import {
  fetchTimerConfigurationAPIThunk,
  fetchTimerStatusThunk,
} from '@/redux/asyncThunks/timerConfiguration'
import { NotificationModal } from '../Notification/NotificationModal'
import { sendSystemMessage } from '@/redux/asyncThunks/notification'
import {
  NotificationDisplayType,
  NotificationVariant,
} from '@/redux/types/notifications'
import { ArrowLeftCircle } from 'lucide-react'
import ROUTES from '@/routes/routes'
import StartSimulationButton from './StartSimulationButton'
import { EndGameMode } from '@/constants/room'
import { cn } from '@/lib/utils'

const QuickControls: React.FC = () => {
  const params = useParams()
  const dispatch = useDispatch<AppDispatch>()

  const { currentRoom, error } = useSelector((state: RootState) => state.room)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const roomId = params.roomId as string

  const { config, status, loading } = useAppSelector(
    (state: RootState) => state.timerConfiguration
  )
  const {
    phaseId,
    roundId,
    phaseDuration,
    phaseStatus,
    isInfinitePhase,
    endTime,
    roundStatus,
    canGoPreviousPhase,
  } = useGamePhase()

  const {
    isEndGameModalOpen,
    setIsEndGameModalOpen,
    isEndingGame,
    isResetModalOpen,
    setIsResetModalOpen,
    controlsLoading,
    timeControlsEnabled,
    setTimeControlsEnabled,
    handleResetGame,
    handleResetClick,
    handleEndGame,
    handleStartNextRound,
    handleEndRound,
    handleEndPhase,
    handlePreviousPhase,
    handlePauseGame,
    handleEndSimulation,
    isEndSimulationModalOpen,
    setIsEndSimulationModalOpen,
    isEndingSimulation,
  } = useRoomControls(roomId, phaseId, roundId, roundStatus)

  const timerWebsocketConnected = useAppSelector(
    (state) => state.timerWS.isConnected
  )

  const handleSendNotification = async (
    message: string,
    displayType: NotificationDisplayType,
    variant: NotificationVariant
  ) => {
    try {
      dispatch(sendSystemMessage({ roomId, message, displayType, variant }))
    } catch (error) {
      console.error('Failed to send broadcast message:', error)
      throw error
    }
  }

  const handleNotifyClick = () => {
    if (currentRoom?.is_simulating !== true) {
      setIsNotificationModalOpen(true)
    }
  }

  useEffect(() => {
    if (roomId && timerWebsocketConnected) {
      const intervalId = setInterval(() => {
        dispatch(fetchTimerStatusThunk(roomId))
      }, 5000)

      return () => clearInterval(intervalId)
    }
  }, [dispatch, roomId, timerWebsocketConnected])

  useEffect(() => {
    if (roomId) {
      dispatch(fetchTimerConfigurationAPIThunk(roomId))
    }
  }, [dispatch, roomId])

  const fetchRoomData = useCallback(
    async (isPollingCall = false) => {
      if (roomId) {
        try {
          await Promise.all([
            dispatch(getRoomDetails(roomId)),
            dispatch(getRoomMembers(roomId)),
          ])
        } catch (error) {
          console.error(
            `Failed to ${isPollingCall ? 'poll' : 'fetch'} room data:`,
            error
          )
        }
      }
    },
    [dispatch, roomId]
  )

  useEffect(() => {
    fetchRoomData(false)
  }, [roomId])

  if (!currentRoom) {
    return (
      <div>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  }

  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const isGameInProgress = isGameRoomSessionActive(currentRoom?.status)

  const handleResetGameWrapper = async () => {
    await handleResetGame(currentRoom)
  }

  const handleEndGameWrapper = async () => {
    await handleEndGame(currentRoom)
  }

  const handleResetClickWrapper = () => {
    handleResetClick(currentRoom)
  }

  const handleStartNextRoundWrapper = () => {
    handleStartNextRound(isGameCompleted)
  }

  const handleEndRoundWrapper = () => {
    handleEndRound(isGameCompleted)
  }

  const handleEndPhaseWrapper = () => {
    handleEndPhase(isGameCompleted)
  }

  const handlePauseGameWrapper = () => {
    handlePauseGame(isGameCompleted)
  }

  const handlePreviousPhaseWrapper = () => {
    handlePreviousPhase(isGameCompleted)
  }

  const handleEndSimulationWrapper = async () => {
    await handleEndSimulation(currentRoom)
  }

  return (
    <div className='w-full px-24 pb-10'>
      <div className='mt-5'>
        <div className='flex items-center gap-x-2 mb-4'>
          <NavLink
            to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}
            className='flex-shrink-0'
          >
            <ArrowLeftCircle className='text-dark-blue hover:text-dark-blue-hovered' />
          </NavLink>
          <h1 className='text-2xl font-bold text-dark-blue'>Quick Controls</h1>
        </div>

        <div className='flex gap-4 flex-wrap'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={cn(
                    'text-white bg-dark-blue hover:bg-dark-blue-hovered',
                    currentRoom?.is_simulating === true &&
                      'opacity-50 cursor-not-allowed'
                  )}
                  onClick={handleNotifyClick}
                  disabled={currentRoom?.is_simulating === true}
                >
                  <div className='flex items-center gap-2'>
                    <BellIcon className='h-[13px] w-[13px]' />
                    <span className='text-base'>Notify</span>
                  </div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {currentRoom?.is_simulating === true
                    ? 'Notifications are disabled during simulation'
                    : 'Send a broadcast message to all participants in the room'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <NotificationModal
            isOpen={isNotificationModalOpen}
            onClose={() => setIsNotificationModalOpen(false)}
            onSend={handleSendNotification}
          />

          <StartGameButton
            currentRoom={currentRoom!}
            disabled={isGameCompleted}
            className={isGameCompleted ? 'opacity-50 cursor-not-allowed' : ''}
          />

          {(currentRoom?.status === GameRoomStatus.NOT_STARTED ||
            currentRoom?.is_simulating) && (
            <StartSimulationButton currentRoom={currentRoom!} />
          )}

          {currentRoom?.status !== GameRoomStatus.NOT_STARTED &&
            !currentRoom.is_simulating && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                      onClick={handleResetClickWrapper}
                    >
                      <div className='flex items-center gap-2'>
                        <ArrowPathIcon className='h-[13px] w-[13px]' />
                        <span className='text-base'>Reset Game</span>
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Reset the game to its initial state - this will delete all
                      conversations, negotiations, and decisions
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          {/* End Game must also be available for a PAUSED game — hiding it left
              paused/desynced games impossible to end (stuck-room reports). */}
          {isGameRoomSessionActive(currentRoom?.status) &&
            !currentRoom?.ends_in && (
              <EndGameButton
                onEndGame={() => {
                  if (currentRoom?.is_simulating) {
                    setIsEndSimulationModalOpen(true)
                  } else {
                    setIsEndGameModalOpen(true)
                  }
                }}
                disabled={
                  currentRoom?.is_simulating ? isEndingSimulation : isEndingGame
                }
                mode={
                  currentRoom?.is_simulating
                    ? EndGameMode.Simulation
                    : EndGameMode.Game
                }
              />
            )}
        </div>
        <ConfirmationModal
          isOpen={isResetModalOpen}
          onClose={() => setIsResetModalOpen(false)}
          onConfirm={handleResetGameWrapper}
          title='Reset Game'
          description='Are you sure you want to reset this game? This will delete all conversations, negotiations, and decisions, and the game will start from the beginning.'
          confirmButtonText='Reset'
          cancelButtonText='Cancel'
        />
      </div>
      <EndGameModal
        isOpen={isEndGameModalOpen}
        onClose={() => setIsEndGameModalOpen(false)}
        onConfirm={handleEndGameWrapper}
        isLoading={isEndingGame}
      />
      <EndGameModal
        isOpen={isEndSimulationModalOpen}
        onClose={() => setIsEndSimulationModalOpen(false)}
        onConfirm={handleEndSimulationWrapper}
        isLoading={isEndingSimulation}
        mode={EndGameMode.Simulation}
      />
      {isGameRoomSessionActive(currentRoom?.status) && (
        <>
          <div className='mt-2'>
            <div className='text-base'>
              <b>Started At:</b>{' '}
              {currentRoom.start_time
                ? new Date(currentRoom.start_time).toLocaleString('en-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                  })
                : '-'}
              <br />
            </div>
          </div>
        </>
      )}
      <div className='flex gap-6 mt-6'>
        <div className='flex-1'>
          {config && (
            <div className='bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm space-y-6 flex flex-col'>
              <h5 className='text-xl font-bold text-dark-blue'>
                Round Controls
              </h5>
              <div className='flex-1'>
                <TimerControls
                  status={status}
                  endTime={endTime || null}
                  phaseDuration={phaseDuration || null}
                  phaseStatus={phaseStatus || null}
                  roundStatus={roundStatus}
                  mode={config.mode}
                  loading={loading}
                  controlsLoading={controlsLoading}
                  timeControlsEnabled={timeControlsEnabled}
                  onTimeControlsToggle={setTimeControlsEnabled}
                  onStartNextRound={handleStartNextRoundWrapper}
                  onEndRound={handleEndRoundWrapper}
                  onEndPhase={handleEndPhaseWrapper}
                  onPreviousPhase={handlePreviousPhaseWrapper}
                  onPause={handlePauseGameWrapper}
                  canGoPreviousPhase={canGoPreviousPhase}
                  isGameCompleted={isGameCompleted}
                  isGameInProgress={isGameInProgress}
                  isInfinitePhase={isInfinitePhase}
                />
              </div>
              <GameplayLength config={config} />
            </div>
          )}
        </div>

        <div className='flex-1'>
          {config && (
            <div className='bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm h-full flex flex-col'>
              <div className='mb-4'>
                <h5 className='text-xl font-bold text-dark-blue'>Events</h5>
              </div>
              <div className='flex-1'>
                <QuickAddEvent
                  events={currentRoom?.events || []}
                  isGameCompleted={
                    currentRoom?.status === GameRoomStatus.COMPLETED
                  }
                  isGameInProgress={isGameRoomSessionActive(
                    currentRoom?.status
                  )}
                  onEventsUpdate={() => dispatch(getRoomDetails(roomId))}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuickControls
